import React from 'react';

function pluralize(count, singular, plural = `${singular}s`) {
  return count === 1 ? singular : plural;
}

export default function ArchitectureView({
  selectedSystem,
  architecture,
  systemFindingsWithComponent,
  componentReliabilityIssues = new Map(),
  displayMode,
  qualityReason,
  graph,
}) {
  const visibleComponentCount = architecture?.components?.length || 0;
  const relationshipCount = architecture?.relationships?.length || 0;
  const localizedFindingCount = systemFindingsWithComponent.length;
  const reliabilityHotspotCount = componentReliabilityIssues.size;

  return (
    <section className="content-grid single-column">
      <div className="panel detail-panel">
        <div className="panel-header">
          <div>
            <p className="detail-kicker">System map</p>
            <h2>Architecture</h2>
            <p className="panel-copy">
              Explore the runtime-derived component map and understand where reliability issues concentrate.
            </p>
          </div>
          <div className="form-actions">
            <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/components`}>
              Open components
            </a>
            <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/findings`}>
              Review findings
            </a>
          </div>
        </div>

        <div className="findings-summary-grid">
          <div className="detail-card summary-card">
            <span className="summary-card-label">Visible components</span>
            <strong>{visibleComponentCount}</strong>
          </div>
          <div className="detail-card summary-card">
            <span className="summary-card-label">Observed relationships</span>
            <strong>{relationshipCount}</strong>
          </div>
          <div className="detail-card summary-card">
            <span className="summary-card-label">Localized findings</span>
            <strong>{localizedFindingCount}</strong>
          </div>
          <div className="detail-card summary-card">
            <span className="summary-card-label">Reliability hotspots</span>
            <strong>{reliabilityHotspotCount}</strong>
          </div>
        </div>

        {localizedFindingCount ? (
          <section className="findings-priority-band system-map-focus-band">
            <div className="chapter-header">
              <div>
                <p className="chapter-kicker">Follow-up</p>
                <h3>Localized issues linked to components</h3>
              </div>
            </div>
            <div className="stack compact-stack system-map-link-stack">
              {systemFindingsWithComponent.slice(0, 4).map((finding) => (
                <a className="overview-row-link" href={`#/systems/${selectedSystem.id}/findings/${finding.id}`} key={finding.id}>
                  <div>
                    <strong>{finding.title}</strong>
                    <p>{finding.summary}</p>
                  </div>
                  <span className="chip">{finding.severity}</span>
                </a>
              ))}
            </div>
          </section>
        ) : null}

        {architecture && architecture.components.length === 0 ? (
          <div className="empty-state-card">
            <strong>No components defined yet</strong>
            <p>Components help you localize where issues originate. Import more runtime data to make this view more useful.</p>
          </div>
        ) : architecture && displayMode === 'hidden' ? (
          <div className="architecture-quality-card">
            <p className="empty-state">{qualityReason}</p>
            <p className="panel-copy">
              Import more traces, or traces with clearer service, tool, model, or endpoint identities before surfacing components reliably.
            </p>
            <div className="chip-row architecture-diagnostics">
              <span className="chip">{architecture.quality.component_count} components</span>
              <span className="chip">{architecture.quality.relationship_count} relationships</span>
              {architecture.quality.inferred_only_graph ? <span className="chip">Inferred only</span> : null}
            </div>
          </div>
        ) : architecture ? (
          <>
            {displayMode === 'partial' ? (
              <div className="architecture-quality-card architecture-partial-card">
                <p className="empty-state">
                  {architecture.quality?.trimmed_for_display
                    ? 'This graph has been trimmed to the clearest inferred components and relationships for readability.'
                    : 'Some inferred components are lower confidence and are visually de-emphasized.'}
                </p>
                <div className="chip-row architecture-diagnostics">
                  {typeof architecture.quality?.visible_component_count === 'number' ? (
                    <span className="chip">{architecture.quality.visible_component_count} components shown</span>
                  ) : null}
                  {typeof architecture.quality?.visible_relationship_count === 'number' ? (
                    <span className="chip">{architecture.quality.visible_relationship_count} relationships shown</span>
                  ) : null}
                  {typeof architecture.quality?.low_confidence_component_count === 'number' ? (
                    <span className="chip">{architecture.quality.low_confidence_component_count} lower-confidence components</span>
                  ) : null}
                  {typeof architecture.quality?.low_confidence_relationship_count === 'number' ? (
                    <span className="chip">{architecture.quality.low_confidence_relationship_count} lower-confidence relationships</span>
                  ) : null}
                </div>
              </div>
            ) : null}

            <section className="overview-band">
              <div className="chapter-header">
                <div>
                  <p className="chapter-kicker">Map</p>
                  <h3>Component map</h3>
                </div>
              </div>
              {reliabilityHotspotCount ? (
                <p className="overview-note architecture-hotspot-note">
                  Tinted components have active reliability findings linked to them. Darker tint indicates higher severity.
                </p>
              ) : null}
              {graph}
            </section>

            <section className="overview-band">
              <div className="chapter-header">
                <div>
                  <p className="chapter-kicker">Browse</p>
                  <h3>Components in the map</h3>
                </div>
                <a className="pill-link" href={`#/systems/${selectedSystem.id}/components`}>
                  Open component list
                </a>
              </div>
              <div className="stack compact-stack architecture-legend">
                {architecture.components.map((component) => {
                  const reliabilityIssue = componentReliabilityIssues.get(component.id) || null;
                  return (
                    <a
                      className={[
                        'detail-card',
                        'architecture-legend-card',
                        reliabilityIssue ? `architecture-legend-card-issue-${reliabilityIssue.band}` : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      href={`#/systems/${selectedSystem.id}/components/${component.id}`}
                      key={component.id}
                    >
                      <strong className="architecture-legend-title">{component.display_name || component.name}</strong>
                      <p className="architecture-legend-meta">
                        {component.component_type} · {component.status}
                      </p>
                      {reliabilityIssue || component.inferred || component.metadata?.inference_confidence_band ? (
                        <div className="chip-row">
                          {reliabilityIssue ? (
                            <span className={`chip architecture-hotspot-chip architecture-hotspot-chip-${reliabilityIssue.band}`}>
                              {reliabilityIssue.count} active reliability {pluralize(reliabilityIssue.count, 'finding')}
                            </span>
                          ) : null}
                          {component.inferred ? <span className="chip">Inferred</span> : null}
                          {component.metadata?.inference_confidence_band ? (
                            <span className="chip">{component.metadata.inference_confidence_band} confidence</span>
                          ) : null}
                        </div>
                      ) : null}
                    </a>
                  );
                })}
              </div>
            </section>
          </>
        ) : (
          <div className="empty-state-card">
            <strong>No components defined yet</strong>
            <p>Components help you localize where issues originate. Import more runtime data to make this view more useful.</p>
          </div>
        )}
      </div>
    </section>
  );
}
